import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import withStyles from "isomorphic-style-loader/withStyles";
import { adminOtpResend, adminOtpVerify } from "../../utils/fetch";
import {
  loadResendCooldownSeconds,
  RESEND_COOLDOWN_DEFAULT_SECONDS,
} from "../../utils/firebase/twoFactorAuthConfig";
import {
  AdminOtpDeliveryChannel,
  normalizeAdminOtpDeliveryChannel,
} from "../../types/services/IAdminOtp";
import PrimaryButton from "../primaryButton";
import s from "./styles.scss";

const DIGIT_COUNT = 6;
const RESEND_SUCCESS_MS = 4500;

export interface AdminOtpPanelProps {
  authServiceUrl: string;
  challengeId: string;
  onVerified: (completionToken: string) => void | Promise<void>;
  onBack?: () => void;
  intro?: string;
  smsEligible?: boolean;
  initialChannel?: AdminOtpDeliveryChannel;
  forceSmsOnly?: boolean;
  /** When false, render no Back link here — parent should place it (e.g. above the screen title). */
  embedBackLink?: boolean;
  /** After OTP passes, show a short success state while `onVerified` runs (e.g. login complete or password save). */
  showVerifiedSuccessScreen?: boolean;
  /** Title on the post-verify success screen. Defaults to login copy. */
  verifiedSuccessTitle?: string;
  /**
   * Subtext below the title. When omitted, login redirect copy is used. Pass "" to hide the line (spinner still shows).
   */
  verifiedSuccessBlurb?: string;
}

function parseOtpDigits(text: string): string[] {
  const only = text.replace(/\D/g, "").slice(0, DIGIT_COUNT);
  return only.split("");
}

const AdminOtpPanel: React.FC<AdminOtpPanelProps> = ({
  authServiceUrl,
  challengeId,
  onVerified,
  onBack,
  intro = "Enter the 6-digit code we sent to you.",
  smsEligible = false,
  initialChannel = "email",
  forceSmsOnly = false,
  embedBackLink = true,
  showVerifiedSuccessScreen = false,
  verifiedSuccessTitle = "Verification successful!",
  verifiedSuccessBlurb,
}) => {
  const [digits, setDigits] = useState<string[]>(() => Array(DIGIT_COUNT).fill(""));
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(RESEND_COOLDOWN_DEFAULT_SECONDS);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_DEFAULT_SECONDS);
  const [resendSuccess, setResendSuccess] = useState<AdminOtpDeliveryChannel | null>(null);
  const [awaitingRedirect, setAwaitingRedirect] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<AdminOtpDeliveryChannel>(
    normalizeAdminOtpDeliveryChannel(initialChannel)
  );
  const [lastSentChannel, setLastSentChannel] = useState<AdminOtpDeliveryChannel>(
    normalizeAdminOtpDeliveryChannel(initialChannel)
  );
  const [channelHint, setChannelHint] = useState<string | null>(null);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const autoSubmitStartedRef = useRef(false);
  const submitRef = useRef<(e?: React.FormEvent) => Promise<void>>(async () => {});

  const showChannelToggle = !forceSmsOnly && smsEligible;

  const resolvedIntro = useMemo(() => {
    if (forceSmsOnly || selectedChannel === "sms") {
      return "Enter the 6-digit code sent to your phone number.";
    }
    return intro;
  }, [forceSmsOnly, selectedChannel, intro]);

  useEffect(() => {
    loadResendCooldownSeconds().then(seconds => {
      setResendCooldownSeconds(seconds);
    });
  }, []);

  useEffect(() => {
    const channel = normalizeAdminOtpDeliveryChannel(initialChannel);
    setDigits(Array(DIGIT_COUNT).fill(""));
    setError(null);
    setVerifying(false);
    setResending(false);
    setResendIn(resendCooldownSeconds);
    setResendSuccess(null);
    setAwaitingRedirect(false);
    setSelectedChannel(channel);
    setLastSentChannel(channel);
    setChannelHint(null);
    autoSubmitStartedRef.current = false;
    window.requestAnimationFrame(() => inputsRef.current[0]?.focus());
  }, [challengeId, resendCooldownSeconds, initialChannel]);

  useEffect(() => {
    if (!resendSuccess) {
      return;
    }
    const t = window.setTimeout(() => setResendSuccess(null), RESEND_SUCCESS_MS);
    return () => window.clearTimeout(t);
  }, [resendSuccess]);

  useEffect(() => {
    if (resendIn <= 0) {
      return;
    }
    const t = window.setInterval(() => {
      setResendIn(v => (v <= 1 ? 0 : v - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [resendIn, challengeId]);

  const otpString = digits.join("");

  const resolvedSuccessBlurb =
    verifiedSuccessBlurb === undefined
      ? "You're being redirected to the admin portal."
      : verifiedSuccessBlurb;

  const focusAt = useCallback((i: number) => {
    const el = inputsRef.current[Math.max(0, Math.min(DIGIT_COUNT - 1, i))];
    el?.focus();
    el?.select();
  }, []);

  const clearDigitsAndFocusFirst = useCallback(() => {
    setDigits(Array(DIGIT_COUNT).fill(""));
    setError(null);
    window.requestAnimationFrame(() => {
      inputsRef.current[0]?.focus();
    });
  }, []);

  const setDigitAt = useCallback(
    (index: number, value: string) => {
      const d = value.replace(/\D/g, "").slice(-1);
      setDigits(prev => {
        const next = [...prev];
        next[index] = d;
        return next;
      });
      if (d && index < DIGIT_COUNT - 1) {
        window.requestAnimationFrame(() => focusAt(index + 1));
      }
    },
    [focusAt]
  );

  const applyDigitsFromString = useCallback(
    (raw: string) => {
      const parsed = parseOtpDigits(raw);
      setDigits(() => {
        const next = Array(DIGIT_COUNT).fill("") as string[];
        for (let i = 0; i < parsed.length; i++) {
          next[i] = parsed[i];
        }
        return next;
      });
      const last = Math.min(parsed.length, DIGIT_COUNT - 1);
      window.requestAnimationFrame(() => focusAt(parsed.length >= DIGIT_COUNT ? DIGIT_COUNT - 1 : last));
    },
    [focusAt]
  );

  const resendWithChannel = useCallback(
    async (channel: AdminOtpDeliveryChannel) => {
      if (resendIn > 0 || verifying || resending || awaitingRedirect) {
        return;
      }
      setError(null);
      setResendSuccess(null);
      setResending(true);
      try {
        await adminOtpResend(authServiceUrl, challengeId, channel);
        setResendIn(resendCooldownSeconds);
        setLastSentChannel(channel);
        setChannelHint(null);
        setResendSuccess(channel);
        clearDigitsAndFocusFirst();
      } catch (err: any) {
        setError(err?.message || "Could not resend code.");
      } finally {
        setResending(false);
      }
    },
    [
      authServiceUrl,
      challengeId,
      resendIn,
      verifying,
      resending,
      awaitingRedirect,
      resendCooldownSeconds,
      clearDigitsAndFocusFirst,
    ]
  );

  const onChannelSelect = (channel: AdminOtpDeliveryChannel) => {
    if (channel === selectedChannel || verifying || resending || awaitingRedirect) {
      return;
    }
    setError(null);
    setSelectedChannel(channel);
    if (channel !== lastSentChannel && resendIn === 0 && !resending && !verifying) {
      void resendWithChannel(channel);
      return;
    }
    if (channel !== lastSentChannel) {
      setChannelHint(
        `Tap Send again to receive the code via ${channel === "sms" ? "SMS" : "email"} instead.`
      );
    } else {
      setChannelHint(null);
    }
  };

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    const code = otpString.replace(/\D/g, "").slice(0, DIGIT_COUNT);
    if (code.length !== DIGIT_COUNT) {
      setError("Enter the 6-digit code.");
      return;
    }
    setVerifying(true);
    try {
      const r = await adminOtpVerify(authServiceUrl, challengeId, code);
      if (!r.completionToken) {
        throw new Error("Verification incomplete. Try again.");
      }
      if (showVerifiedSuccessScreen) {
        setAwaitingRedirect(true);
      }
      setVerifying(false);
      try {
        await Promise.resolve(onVerified(r.completionToken));
      } catch (postErr: any) {
        if (showVerifiedSuccessScreen) {
          setAwaitingRedirect(false);
        }
        setError(postErr?.message || "Something went wrong. Try again.");
      }
    } catch (err: any) {
      setError(err?.message || "Verification failed.");
    } finally {
      setVerifying(false);
    }
  };
  submitRef.current = submit;

  useEffect(() => {
    const code = digits.join("").replace(/\D/g, "");
    if (code.length !== DIGIT_COUNT) {
      autoSubmitStartedRef.current = false;
      return;
    }
    if (verifying || resending || awaitingRedirect || autoSubmitStartedRef.current) {
      return;
    }
    autoSubmitStartedRef.current = true;
    void submitRef.current();
  }, [digits, verifying, resending, awaitingRedirect]);

  const resend = async () => {
    const channel = forceSmsOnly ? "sms" : selectedChannel;
    await resendWithChannel(channel);
  };

  const onDigitChange = (index: number, value: string) => {
    if (value.length > 1) {
      applyDigitsFromString(value);
      return;
    }
    setDigitAt(index, value);
  };

  const onDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        setDigits(prev => {
          const next = [...prev];
          next[index] = "";
          return next;
        });
      } else if (index > 0) {
        focusAt(index - 1);
        setDigits(prev => {
          const next = [...prev];
          next[index - 1] = "";
          return next;
        });
      }
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      focusAt(index - 1);
      e.preventDefault();
    }
    if (e.key === "ArrowRight" && index < DIGIT_COUNT - 1) {
      focusAt(index + 1);
      e.preventDefault();
    }
  };

  const onDigitPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const t = e.clipboardData.getData("text");
    if (t && /\d/.test(t)) {
      e.preventDefault();
      e.stopPropagation();
      applyDigitsFromString(t);
    }
  };

  const onDigitMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    clearDigitsAndFocusFirst();
  };

  if (awaitingRedirect && showVerifiedSuccessScreen) {
    return (
      <div className={s.panel}>
        <div className={s.successScreen} role="status" aria-live="polite">
          <div className={s.successIcon} aria-hidden>
            <i className="fa-solid fa-circle-check" />
          </div>
          <div className={s.successTitle}>{verifiedSuccessTitle}</div>
          {resolvedSuccessBlurb !== "" ? (
            <p className={s.successBlurb}>{resolvedSuccessBlurb}</p>
          ) : null}
          <div className={s.redirectSpinnerWrap}>
            <CircularProgress size={36} thickness={4} className={s.redirectSpinner} aria-hidden />
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className={s.panel} onSubmit={submit}>
      {onBack && embedBackLink ? (
        <div className={s.backRow}>
          <button type="button" className={s.linkButton} onClick={onBack} disabled={verifying}>
            Back
          </button>
        </div>
      ) : null}

      <p className={s.intro}>{resolvedIntro}</p>

      {showChannelToggle ? (
        <div className={s.channelToggle} role="group" aria-label="Verification delivery method">
          <button
            type="button"
            className={classNamesForChannel("sms", selectedChannel)}
            onClick={() => onChannelSelect("sms")}
            disabled={verifying || resending || awaitingRedirect}
          >
            SMS
          </button>
          <button
            type="button"
            className={classNamesForChannel("email", selectedChannel)}
            onClick={() => onChannelSelect("email")}
            disabled={verifying || resending || awaitingRedirect}
          >
            Email
          </button>
        </div>
      ) : null}

      <label className={s.fieldLabel} htmlFor="adminOtpDigit-0">
        Verification code
      </label>

      <div className={s.digitsRow} onPaste={onDigitPaste}>
        {Array.from({ length: DIGIT_COUNT }).map((_, i) => (
          <input
            key={i}
            id={i === 0 ? "adminOtpDigit-0" : undefined}
            ref={el => {
              inputsRef.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={i === 0 ? DIGIT_COUNT : 1}
            className={s.digitInput}
            value={digits[i]}
            disabled={verifying}
            onChange={e => onDigitChange(i, e.target.value)}
            onKeyDown={e => onDigitKeyDown(i, e)}
            onMouseDown={onDigitMouseDown}
            onPaste={onDigitPaste}
            onFocus={e => e.target.select()}
            aria-label={`Digit ${i + 1} of ${DIGIT_COUNT}`}
          />
        ))}
      </div>

      {error ? <div className={s.error}>{error}</div> : null}
      {channelHint ? <div className={s.channelHint}>{channelHint}</div> : null}

      <PrimaryButton
        type="submit"
        className={s.verifyButton}
        disabled={verifying}
        loading={verifying}
        loadingText="Verifying…"
      >
        Verify
      </PrimaryButton>

      <div className={s.resendRow}>
        <div className={s.resendInner}>
          {resending ? (
            <CircularProgress size={22} thickness={4} className={s.resendSpinner} />
          ) : null}
          <button
            type="button"
            className={s.linkButton}
            onClick={resend}
            disabled={verifying || resending || resendIn > 0 || awaitingRedirect}
          >
            {resendIn > 0 ? `Send again (${resendIn}s)` : "Send again"}
          </button>
        </div>
      </div>
      {resendSuccess ? (
        <div className={s.resendSuccess} role="status">
          {resendSuccess === "sms"
            ? "✅ A new code was sent via SMS."
            : "✅ A new code was sent to your email."}
        </div>
      ) : null}
    </form>
  );
};

function classNamesForChannel(
  channel: AdminOtpDeliveryChannel,
  selected: AdminOtpDeliveryChannel
): string {
  return channel === selected ? `${s.channelOption} ${s.channelOptionActive}` : s.channelOption;
}

export default withStyles(s)(AdminOtpPanel);
